import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Search, QrCode, Stamp, Gift, Users, Star, TrendingUp, Calendar, UserPlus, Pencil, Trash2, Award, AlertTriangle, Loader2, UtensilsCrossed, CupSoda, Cookie, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STAMPS_MAX = 10;

function generatePrintHTML(clients) {
  const cards = clients.map(c => `
    <div class="card">
      <div class="card-header">CHANA POKE THAI</div>
      <div class="card-subtitle">Programme Fidelite</div>
      <div class="qr-wrap">
        <img src="${API}/api/fidelite/clients/${c.id}/qr" alt="QR" class="qr" crossorigin="anonymous" />
      </div>
      <div class="card-name">${c.prenom} ${c.nom}</div>
      <div class="card-info">Scannez pour voir vos points</div>
      <div class="card-stamps">
        <span class="stamp-line"><span class="stamp-icon">&#127858;</span> Plat <span class="stamp-dots">${'&#9733;'.repeat(c.stamps_plat || 0)}${'&#9734;'.repeat(STAMPS_MAX - (c.stamps_plat || 0))}</span></span>
        <span class="stamp-line"><span class="stamp-icon">&#129380;</span> Bubble Tea <span class="stamp-dots">${'&#9733;'.repeat(c.stamps_bubble_tea || 0)}${'&#9734;'.repeat(STAMPS_MAX - (c.stamps_bubble_tea || 0))}</span></span>
        <span class="stamp-line"><span class="stamp-icon">&#127849;</span> Mochi <span class="stamp-dots">${'&#9733;'.repeat(c.stamps_mochi || 0)}${'&#9734;'.repeat(STAMPS_MAX - (c.stamps_mochi || 0))}</span></span>
      </div>
      <div class="card-footer">10 tampons = 1 offert</div>
    </div>
  `).join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Cartes Fidelite - Chana Poke Thai</title>
<style>
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: white; }
  .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 8px; }
  .card {
    border: 2px solid #222; border-radius: 14px; padding: 16px 12px 12px;
    text-align: center; page-break-inside: avoid;
    background: linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    color: white; min-height: 280px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .card-header {
    font-size: 14px; font-weight: 800; letter-spacing: 2px;
    color: #f59e0b; margin-bottom: 1px;
  }
  .card-subtitle { font-size: 9px; color: #94a3b8; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase; }
  .qr-wrap {
    background: white; border-radius: 10px; padding: 6px; display: inline-block; margin-bottom: 8px;
  }
  .qr { width: 100px; height: 100px; display: block; }
  .card-name { font-size: 15px; font-weight: 700; margin-bottom: 2px; }
  .card-info { font-size: 8px; color: #94a3b8; margin-bottom: 8px; }
  .card-stamps { font-size: 9px; text-align: left; width: 100%; padding: 0 6px; }
  .stamp-line { display: flex; align-items: center; gap: 4px; margin-bottom: 2px; color: #cbd5e1; }
  .stamp-icon { font-size: 11px; width: 16px; text-align: center; }
  .stamp-dots { margin-left: auto; font-size: 10px; letter-spacing: 1px; color: #f59e0b; }
  .card-footer { font-size: 8px; color: #f59e0b; margin-top: 8px; font-weight: 600; letter-spacing: 0.5px; }
</style>
</head>
<body>
<div class="grid">${cards}</div>
</body>
</html>`;
}


const CARTE_CONFIG = {
  plat: { label: 'Plat', icon: UtensilsCrossed, color: 'amber', bg: 'bg-amber-400', border: 'border-amber-500', light: 'bg-amber-50', text: 'text-amber-700' },
  bubble_tea: { label: 'Bubble Tea', icon: CupSoda, color: 'purple', bg: 'bg-purple-400', border: 'border-purple-500', light: 'bg-purple-50', text: 'text-purple-700' },
  mochi: { label: 'Mochi', icon: Cookie, color: 'pink', bg: 'bg-pink-400', border: 'border-pink-500', light: 'bg-pink-50', text: 'text-pink-700' },
};

function StampDots({ count, max, colorBg, colorBorder }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <div key={i} className={`w-2.5 h-2.5 rounded-full border ${i < count ? `${colorBg} ${colorBorder}` : 'bg-muted border-muted-foreground/20'}`} />
      ))}
    </div>
  );
}

export function FidelitePage() {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [detailClient, setDetailClient] = useState(null);
  const [stampDialog, setStampDialog] = useState(null);
  const [stampCarte, setStampCarte] = useState('plat');
  const [stampMontant, setStampMontant] = useState('');
  const [printOpen, setPrintOpen] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState(new Set());

  const [form, setForm] = useState({ nom: '', prenom: '', telephone: '', email: '', date_naissance: '', notes: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const [cRes, sRes] = await Promise.all([
        api.get('/fidelite/clients', { params }),
        api.get('/fidelite/stats'),
      ]);
      setClients(cRes.data);
      setStats(sRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditClient(null);
    setForm({ nom: '', prenom: '', telephone: '', email: '', date_naissance: '', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditClient(c);
    setForm({ nom: c.nom, prenom: c.prenom, telephone: c.telephone, email: c.email, date_naissance: c.date_naissance || '', notes: c.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.prenom.trim()) { toast.error('Prenom requis'); return; }
    try {
      if (editClient) {
        await api.put(`/fidelite/clients/${editClient.id}`, form);
        toast.success('Client mis a jour');
      } else {
        await api.post('/fidelite/clients', form);
        toast.success('Client ajoute');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await api.delete(`/fidelite/clients/${id}`);
      toast.success('Client supprime');
      if (detailClient?.id === id) setDetailClient(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const openDetail = async (c) => {
    try {
      const res = await api.get(`/fidelite/clients/${c.id}`);
      setDetailClient(res.data);
    } catch { toast.error('Erreur'); }
  };

  const openStamp = (c) => {
    setStampDialog(c);
    setStampCarte('plat');
    setStampMontant('');
  };

  const handleStamp = async () => {
    if (!stampDialog) return;
    try {
      const res = await api.post(`/fidelite/clients/${stampDialog.id}/stamp`, { carte: stampCarte, montant: parseFloat(stampMontant) || 0 });
      if (res.data.reward_earned) {
        toast.success(`BRAVO ! ${res.data.message}`, { duration: 5000 });
      } else {
        toast.success(res.data.message);
      }
      setStampDialog(null);
      fetchData();
      if (detailClient?.id === stampDialog.id) openDetail(stampDialog);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUseReward = async (clientId, carteId) => {
    try {
      await api.post(`/fidelite/clients/${clientId}/use-reward`, { carte: carteId });
      toast.success('Recompense utilisee !');
      fetchData();
      if (detailClient?.id === clientId) openDetail({ id: clientId });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const s = stats || {};

  if (loading && !clients.length) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="fidelite-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500" /> Programme Fidelite
          </h1>
          <p className="text-muted-foreground text-sm">3 cartes : Plat, Bubble Tea, Mochi — 10 tampons = 1 offert</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setSelectedForPrint(new Set(clients.map(c => c.id))); setPrintOpen(true); }} data-testid="print-qr-btn">
            <Printer className="w-4 h-4 mr-2" /> Imprimer QR
          </Button>
          <Button onClick={openCreate} data-testid="add-client-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Nouveau client
          </Button>
        </div>
      </div>

      <Tabs defaultValue="clients">
        <TabsList>
          <TabsTrigger value="clients" data-testid="tab-clients"><Users className="w-4 h-4 mr-1" /> Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><TrendingUp className="w-4 h-4 mr-1" /> Tableau de bord</TabsTrigger>
          <TabsTrigger value="parrainage" data-testid="tab-parrainage"><Gift className="w-4 h-4 mr-1" /> Parrainage</TabsTrigger>
        </TabsList>

        {/* CLIENTS TAB */}
        <TabsContent value="clients" className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10" data-testid="search-client" />
          </div>

          {clients.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{search ? 'Aucun resultat' : 'Aucun client'}</h3>
                <Button onClick={openCreate}><UserPlus className="w-4 h-4 mr-2" /> Ajouter un client</Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Telephone</TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1"><UtensilsCrossed className="w-3 h-3" /> Plat</div>
                      </TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1"><CupSoda className="w-3 h-3" /> B. Tea</div>
                      </TableHead>
                      <TableHead className="text-center">
                        <div className="flex items-center justify-center gap-1"><Cookie className="w-3 h-3" /> Mochi</div>
                      </TableHead>
                      <TableHead className="text-center">Visites</TableHead>
                      <TableHead className="w-[140px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map(c => (
                      <TableRow key={c.id} className="cursor-pointer hover:bg-muted/30" data-testid={`client-row-${c.id}`}>
                        <TableCell onClick={() => openDetail(c)}>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{c.prenom} {c.nom}</span>
                            {c.date_naissance && isUpcomingBirthday(c.date_naissance) && (
                              <Badge className="bg-pink-100 text-pink-700 text-xs">Anniversaire</Badge>
                            )}
                          </div>
                          {c.email && <span className="text-xs text-muted-foreground">{c.email}</span>}
                        </TableCell>
                        <TableCell className="text-sm">{c.telephone || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-col items-center gap-0.5">
                            <StampDots count={c.stamps_plat || 0} max={STAMPS_MAX} colorBg="bg-amber-400" colorBorder="border-amber-500" />
                            <span className="text-[10px] tabular-nums text-muted-foreground">{c.stamps_plat || 0}/{STAMPS_MAX}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-center gap-0.5">
                            <StampDots count={c.stamps_bubble_tea || 0} max={STAMPS_MAX} colorBg="bg-purple-400" colorBorder="border-purple-500" />
                            <span className="text-[10px] tabular-nums text-muted-foreground">{c.stamps_bubble_tea || 0}/{STAMPS_MAX}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-center gap-0.5">
                            <StampDots count={c.stamps_mochi || 0} max={STAMPS_MAX} colorBg="bg-pink-400" colorBorder="border-pink-500" />
                            <span className="text-[10px] tabular-nums text-muted-foreground">{c.stamps_mochi || 0}/{STAMPS_MAX}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">{c.total_visites || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => openStamp(c)} data-testid={`stamp-${c.id}`}>
                              <Stamp className="w-3 h-3 mr-1" /> Tampon
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openDetail(c)}><QrCode className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Clients" value={s.total_clients || 0} color="text-blue-600" />
            <StatCard icon={Stamp} label="Total tampons" value={s.total_visites || 0} color="text-green-600" />
            <StatCard icon={Gift} label="Recompenses gagnees" value={s.total_rewards_gagnes || 0} color="text-amber-600" />
            <StatCard icon={Award} label="A utiliser" value={s.total_rewards_pending || 0} color="text-purple-600" />
          </div>

          {/* Per-carte stats */}
          {s.cartes_stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(CARTE_CONFIG).map(([id, cfg]) => {
                const cs = s.cartes_stats[id] || {};
                const Icon = cfg.icon;
                return (
                  <Card key={id} className={`border-2 ${cfg.light}`} data-testid={`stats-carte-${id}`}>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`w-5 h-5 ${cfg.text}`} />
                        <span className={`font-semibold ${cfg.text}`}>{cfg.label}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-2xl font-bold tabular-nums">{cs.visites || 0}</p>
                          <p className="text-[10px] text-muted-foreground">Tampons</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold tabular-nums text-green-600">{cs.rewards_gagnes || 0}</p>
                          <p className="text-[10px] text-muted-foreground">Gagnes</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold tabular-nums text-purple-600">{cs.rewards_pending || 0}</p>
                          <p className="text-[10px] text-muted-foreground">A utiliser</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {s.birthday_clients?.length > 0 && (
            <Card className="border-2 border-pink-200 bg-pink-50/50" data-testid="birthday-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-pink-700">
                  <Calendar className="w-5 h-5" /> Anniversaires ce mois
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {s.birthday_clients.map(c => (
                    <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-pink-200">
                      <span className="font-medium">{c.prenom} {c.nom}</span>
                      <Badge className="bg-pink-100 text-pink-700 text-xs">{c.date_naissance}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {s.inactive_clients?.length > 0 && (
            <Card className="border-2 border-amber-200" data-testid="inactive-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" /> Clients inactifs (+30 jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {s.inactive_clients.map(c => (
                    <Button key={c.id} variant="outline" size="sm" onClick={() => openStamp(c)}>
                      {c.prenom} {c.nom} <Stamp className="w-3 h-3 ml-1" />
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {s.top_clients?.length > 0 && (
            <Card data-testid="top-clients-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Top clients</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Client</TableHead><TableHead className="text-center">Visites</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {s.top_clients.map((c, i) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-bold text-amber-600">{i + 1}</TableCell>
                        <TableCell className="font-medium">{c.prenom} {c.nom}</TableCell>
                        <TableCell className="text-center font-bold">{c.total_visites}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PARRAINAGE TAB */}
        <TabsContent value="parrainage" className="space-y-4">
          <ParrainageTab api={api} clients={clients} onRefresh={fetchData} />
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT CLIENT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editClient ? 'Modifier' : 'Nouveau'} client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Prenom *</Label><Input value={form.prenom} onChange={e => setForm(f => ({...f, prenom: e.target.value}))} placeholder="Ex: Marie" data-testid="client-prenom" /></div>
              <div className="space-y-1"><Label>Nom</Label><Input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} placeholder="Ex: Dupont" data-testid="client-nom" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Telephone</Label><Input value={form.telephone} onChange={e => setForm(f => ({...f, telephone: e.target.value}))} placeholder="06 12 34 56 78" data-testid="client-tel" /></div>
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="marie@email.com" data-testid="client-email" /></div>
            </div>
            <div className="space-y-1"><Label>Date de naissance</Label><Input type="date" value={form.date_naissance} onChange={e => setForm(f => ({...f, date_naissance: e.target.value}))} data-testid="client-birthday" /></div>
            <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Ex: Allergie noix..." data-testid="client-notes" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="save-client-btn">{editClient ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STAMP DIALOG — choose carte */}
      <Dialog open={!!stampDialog} onOpenChange={() => setStampDialog(null)}>
        <DialogContent className="max-w-md" data-testid="stamp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stamp className="w-5 h-5 text-amber-500" /> Ajouter un tampon</DialogTitle>
          </DialogHeader>
          {stampDialog && (
            <div className="space-y-5">
              <p className="text-center text-lg font-semibold">{stampDialog.prenom} {stampDialog.nom}</p>

              {/* Carte selection */}
              <div className="space-y-2">
                <Label>Quelle carte ?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(CARTE_CONFIG).map(([id, cfg]) => {
                    const Icon = cfg.icon;
                    const currentStamps = stampDialog[`stamps_${id}`] || 0;
                    const isSelected = stampCarte === id;
                    return (
                      <button key={id} type="button"
                        className={`p-3 rounded-xl border-2 text-center transition-all ${isSelected ? `${cfg.light} ${cfg.border} ring-2 ring-offset-1 ring-${cfg.color}-300` : 'border-muted hover:border-muted-foreground/30'}`}
                        onClick={() => setStampCarte(id)} data-testid={`select-carte-${id}`}>
                        <Icon className={`w-6 h-6 mx-auto mb-1 ${isSelected ? cfg.text : 'text-muted-foreground'}`} />
                        <p className={`text-sm font-semibold ${isSelected ? cfg.text : ''}`}>{cfg.label}</p>
                        <div className="flex justify-center gap-0.5 mt-1">
                          {Array.from({ length: STAMPS_MAX }, (_, i) => (
                            <div key={i} className={`w-2 h-2 rounded-full ${i < currentStamps ? cfg.bg : 'bg-muted'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{currentStamps}/{STAMPS_MAX}</p>
                        {currentStamps === STAMPS_MAX - 1 && <Badge className="mt-1 bg-green-100 text-green-700 text-[10px]">Prochain = cadeau !</Badge>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <Label>Montant commande (optionnel)</Label>
                <Input type="number" step="0.01" value={stampMontant} onChange={e => setStampMontant(e.target.value)} placeholder="12.50" data-testid="stamp-montant" />
              </div>

              <Button className="w-full" size="lg" onClick={handleStamp} data-testid="confirm-stamp-btn">
                <Stamp className="w-5 h-5 mr-2" /> Valider le tampon {CARTE_CONFIG[stampCarte]?.label}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CLIENT DETAIL */}
      <Dialog open={!!detailClient} onOpenChange={() => setDetailClient(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
          {detailClient && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{detailClient.prenom} {detailClient.nom}</span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(detailClient)}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(detailClient.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {detailClient.telephone && <div><span className="text-muted-foreground">Tel :</span> {detailClient.telephone}</div>}
                  {detailClient.email && <div><span className="text-muted-foreground">Email :</span> {detailClient.email}</div>}
                  {detailClient.date_naissance && <div><span className="text-muted-foreground">Anniversaire :</span> {detailClient.date_naissance}</div>}
                  <div><span className="text-muted-foreground">Visites :</span> <strong>{detailClient.total_visites}</strong></div>
                </div>
                {detailClient.notes && <p className="text-sm text-muted-foreground italic">{detailClient.notes}</p>}

                <Separator />

                {/* 3 cartes */}
                <div className="space-y-3">
                  <p className="text-sm font-semibold">Cartes de fidelite</p>
                  {Object.entries(CARTE_CONFIG).map(([id, cfg]) => {
                    const Icon = cfg.icon;
                    const stamps = detailClient[`stamps_${id}`] || 0;
                    return (
                      <div key={id} className={`p-3 rounded-xl border-2 ${cfg.light} ${cfg.border}`} data-testid={`detail-carte-${id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-5 h-5 ${cfg.text}`} />
                            <span className={`font-semibold ${cfg.text}`}>{cfg.label}</span>
                          </div>
                          <span className={`text-sm font-bold ${cfg.text}`}>{stamps}/{STAMPS_MAX}</span>
                        </div>
                        <div className="flex justify-center gap-1.5 mb-2">
                          {Array.from({ length: STAMPS_MAX }, (_, i) => (
                            <div key={i} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${i < stamps ? `${cfg.bg} ${cfg.border} text-white` : 'bg-white/60 border-muted-foreground/20 text-muted-foreground'}`}>
                              {i + 1}
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-center gap-2">
                          <Button size="sm" onClick={() => { setStampDialog(detailClient); setStampCarte(id); setStampMontant(''); }}>
                            <Stamp className="w-3 h-3 mr-1" /> Tampon
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleUseReward(detailClient.id, id)}>
                            <Gift className="w-3 h-3 mr-1" /> Utiliser
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator />

                {/* QR Code */}
                <div className="text-center space-y-2">
                  <p className="text-sm font-medium">QR Code client</p>
                  <img src={`${API}/api/fidelite/clients/${detailClient.id}/qr`} alt="QR Code"
                    className="w-28 h-28 mx-auto border rounded-lg" data-testid="qr-code-img" />
                </div>

                {/* History */}
                {detailClient.visites?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-2">Historique ({detailClient.visites.length})</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {detailClient.visites.slice(0, 20).map(v => {
                          const carteCfg = CARTE_CONFIG[v.carte] || {};
                          const CarteIcon = carteCfg.icon || Stamp;
                          return (
                            <div key={v.id} className={`flex items-center justify-between text-xs p-2 rounded ${v.type === 'reward_gagne' ? 'bg-green-50 text-green-700' : v.type === 'reward_utilise' ? 'bg-purple-50 text-purple-700' : 'bg-muted/30'}`}>
                              <div className="flex items-center gap-2">
                                <CarteIcon className="w-3 h-3" />
                                <span>{v.notes || (v.type === 'visite' ? `Tampon ${carteCfg.label || ''}` : v.type === 'reward_gagne' ? 'Recompense gagnee' : 'Recompense utilisee')}</span>
                              </div>
                              <span className="text-muted-foreground">{new Date(v.date).toLocaleDateString('fr-FR')}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* PRINT QR DIALOG */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="print-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer className="w-5 h-5" /> Imprimer les cartes QR</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Select clients */}
            <div className="flex items-center justify-between">
              <Label>{selectedForPrint.size}/{clients.length} client(s) selectionne(s)</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedForPrint(new Set(clients.map(c => c.id)))}>Tous</Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedForPrint(new Set())}>Aucun</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {clients.map(c => (
                <button key={c.id} type="button" onClick={() => {
                  const next = new Set(selectedForPrint);
                  if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                  setSelectedForPrint(next);
                }} className={`px-3 py-1 rounded-full text-sm border transition-all ${selectedForPrint.has(c.id) ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 border-muted'}`}>
                  {c.prenom} {c.nom}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Fermer</Button></DialogClose>
            <Button onClick={() => {
              const w = window.open('', '_blank');
              const selectedClients = clients.filter(c => selectedForPrint.has(c.id));
              w.document.write(generatePrintHTML(selectedClients));
              w.document.close();
              w.onload = () => { w.print(); };
            }} disabled={selectedForPrint.size === 0} data-testid="do-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Imprimer {selectedForPrint.size} carte(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card><CardContent className="pt-5 pb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className={`text-3xl font-bold tabular-nums ${color}`}>{value}</p>
    </CardContent></Card>
  );
}

function isUpcomingBirthday(dateStr) {
  if (!dateStr) return false;
  const now = new Date();
  const parts = dateStr.split('-');
  if (parts.length < 3) return false;
  return parseInt(parts[1]) === (now.getMonth() + 1) && Math.abs(parseInt(parts[2]) - now.getDate()) <= 7;
}

// ===== PARRAINAGE =====
function ParrainageTab({ api, clients, onRefresh }) {
  const [stats, setStats] = useState(null);
  const [filleulDialog, setFilleulDialog] = useState(false);
  const [parrainId, setParrainId] = useState('');
  const [form, setForm] = useState({ prenom: '', nom: '', telephone: '', email: '', date_naissance: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const res = await api.get('/insights/parrainage/stats'); setStats(res.data); } catch {}
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const creerFilleul = async () => {
    if (!parrainId || !form.prenom) { toast.error('Parrain et prenom requis'); return; }
    setSaving(true);
    try {
      await api.post('/insights/parrainage/creer-filleul', { parrain_id: parrainId, ...form });
      toast.success('Filleul cree avec 1 tampon offert !');
      setFilleulDialog(false);
      setForm({ prenom: '', nom: '', telephone: '', email: '', date_naissance: '', notes: '' });
      setParrainId('');
      onRefresh();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const validerParrainage = async (filleulId) => {
    try {
      await api.post(`/insights/parrainage/valider/${filleulId}`);
      toast.success('Parrain recompense avec 1 tampon bonus !');
      onRefresh();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Liste filleuls actifs
  const filleuls = clients.filter(c => c.parrain_id);
  const eligibles = filleuls.filter(f => !f.parrainage_valide && (f.total_visites || 0) >= 3);

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Gift} label="Parrainages actifs" value={stats?.total_parrainages || 0} color="text-pink-600" />
        <StatCard icon={Award} label="Valides (+1 tampon)" value={stats?.parrainages_valides || 0} color="text-green-600" />
        <StatCard icon={AlertTriangle} label="Eligibles a valider" value={eligibles.length} color={eligibles.length > 0 ? 'text-amber-600' : 'text-muted-foreground'} />
        <StatCard icon={UserPlus} label="Parrains actifs" value={stats?.top_parrains?.length || 0} color="text-blue-600" />
      </div>

      <Card className="border-dashed border-2">
        <CardContent className="pt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-sm">🎁 Programme parrainage</p>
            <p className="text-xs text-muted-foreground">Le filleul recoit 1 tampon a l'inscription. Apres 3 visites, le parrain gagne 1 tampon bonus.</p>
          </div>
          <Button onClick={() => setFilleulDialog(true)} data-testid="add-filleul-btn"><Gift className="w-4 h-4 mr-1" /> Nouveau filleul</Button>
        </CardContent>
      </Card>

      {/* Eligibles */}
      {eligibles.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/30">
          <CardHeader className="pb-2"><CardTitle className="text-base">Filleuls eligibles a recompenser</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {eligibles.map(f => (
              <div key={f.id} className="flex items-center justify-between p-2 rounded border">
                <div>
                  <p className="font-medium text-sm">{f.prenom} {f.nom}</p>
                  <p className="text-xs text-muted-foreground">Parrain: {f.parrain_nom} • {f.total_visites} visites</p>
                </div>
                <Button size="sm" onClick={() => validerParrainage(f.id)} data-testid={`valider-parrainage-${f.id}`}><Award className="w-4 h-4 mr-1" /> Valider</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top parrains */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Top parrains</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Parrain</TableHead>
              <TableHead className="text-right">Filleuls</TableHead>
              <TableHead className="text-right">Valides</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(stats?.top_parrains || []).length === 0 ? <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">Aucun parrain encore</TableCell></TableRow> :
                stats.top_parrains.map(p => (
                  <TableRow key={p.parrain_id}>
                    <TableCell className="font-medium">{p.parrain_nom || '—'}</TableCell>
                    <TableCell className="text-right">{p.filleuls}</TableCell>
                    <TableCell className="text-right"><Badge className="bg-green-100 text-green-700">{p.valides}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog filleul */}
      <Dialog open={filleulDialog} onOpenChange={setFilleulDialog}>
        <DialogContent data-testid="filleul-dialog">
          <DialogHeader><DialogTitle>Parrainer un nouveau client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Parrain *</Label>
              <Select value={parrainId} onValueChange={setParrainId}>
                <SelectTrigger data-testid="parrain-select"><SelectValue placeholder="Choisir le parrain existant" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom} {c.telephone ? `(${c.telephone})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Prenom filleul *</Label><Input value={form.prenom} onChange={e => setForm(f => ({...f, prenom: e.target.value}))} data-testid="filleul-prenom" /></div>
              <div className="space-y-1"><Label>Nom</Label><Input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Telephone</Label><Input value={form.telephone} onChange={e => setForm(f => ({...f, telephone: e.target.value}))} placeholder="06..." /></div>
              <div className="space-y-1"><Label>Email</Label><Input value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={creerFilleul} disabled={saving} data-testid="save-filleul-btn"><Gift className="w-4 h-4 mr-1" /> {saving ? 'Creation...' : 'Parrainer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
